<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import { useLanguage } from '#composables/useLanguage';
import { formatUnit, type DateRangeType } from '#domain/core/filter/period';
import type { Dayjs } from 'dayjs';
import { computed } from 'vue';
import VueFeather from 'vue-feather';
import { PERIOD_CUSTOM_OPTION } from './constants/periodField.constant';
import PeriodDateInputs from './PeriodDateInputs.vue';
import PeriodPresetSidebar from './PeriodPresetSidebar.vue';
import RangeCalendars from './RangeCalendars.vue';
import type { PeriodOptionType } from './types/periodField.type';
import type { RangeBoundType } from './types/periodPopover.type';
import type { PeriodPopoverPropsType } from './types/periodPopoverProps.type';
import { applyRangeBound } from './utils/periodPopover.utils';

const { name, granularity, interval, withPresets, options, selectedOption, range } =
  defineProps<PeriodPopoverPropsType>();

const emit = defineEmits<{
  'update:selectedOption': [option: PeriodOptionType];
  'update:range': [range: DateRangeType];
  validate: [];
  cancel: [];
}>();

const { t } = useLanguage();

const isCustomSelected = computed(() => selectedOption === PERIOD_CUSTOM_OPTION);

function updateBound(bound: RangeBoundType, date: Dayjs) {
  emit('update:range', applyRangeBound(range, bound, date, granularity, interval));
}
</script>

<template>
  <div :data-testid="`${name}-popover`" class="w-full bg-white rounded-lg shadow-2xl border border-slate-200 flex">
    <PeriodPresetSidebar
      v-if="withPresets"
      class="w-40 shrink-0 border-r border-slate-200 p-2"
      :name="`${name}-option`"
      :options="options"
      :model-value="selectedOption"
      @update:model-value="emit('update:selectedOption', $event)"
    />

    <div class="flex-1 min-w-0 p-3 flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="flex flex-col">
            <span class="font-bold text-sm">{{ range ? formatUnit(range.start, granularity, t) : '--' }}</span>
            <span class="text-xs text-slate-500">{{ t('filters.period.startDate') }}</span>
          </div>

          <VueFeather type="arrow-right" size="16" />

          <div class="flex flex-col">
            <span class="font-bold text-sm">{{ range ? formatUnit(range.end, granularity, t) : '--' }}</span>
            <span class="text-xs text-slate-500">{{ t('filters.period.endDate') }}</span>
          </div>
        </div>

        <button type="button" :data-testid="`${name}-close`" @click="emit('cancel')">
          <VueFeather type="x" size="16" />
        </button>
      </div>

      <PeriodDateInputs
        :name="name"
        :start="range?.start ?? null"
        :end="range?.end ?? null"
        :disabled="!isCustomSelected"
        @update:start="(date) => updateBound('start', date)"
        @update:end="(date) => updateBound('end', date)"
      />

      <div class="overflow-x-auto">
        <RangeCalendars
          class="shrink-0"
          :name="name"
          :granularity="granularity"
          :model-value="range"
          :min-date="interval.start"
          :max-date="interval.end"
          :disabled="!isCustomSelected"
          @update:model-value="emit('update:range', $event)"
        />
      </div>

      <div class="flex justify-end gap-3">
        <ButtonBase variant="secondary" :test-id="`${name}-cancel`" @click="emit('cancel')">
          {{ t('commonLabel.cancel') }}
        </ButtonBase>
        <ButtonBase variant="primary" :test-id="`${name}-validate`" :disabled="!range" @click="emit('validate')">
          {{ t('filters.period.validate') }}
        </ButtonBase>
      </div>
    </div>
  </div>
</template>
